import {RENDER_MODE} from '../vocabulary.js';

/** @typedef {import('../../shell/types/client.types.js').ClientTemplateSummary} ClientTemplateSummary */

/**
 * @param {string} mode
 * @returns {string} display label; unknown modes read as a full page
 */
export function templateModeLabel(mode) {
  switch (mode) {
    case RENDER_MODE.INTERNAL:
      return 'Internal';
    case RENDER_MODE.INDEX_FRAGMENT:
      return 'Index Fragment';
    default:
      return 'Full Page';
  }
}

/**
 * @param {string} mode
 * @returns {string} suffix of the `template-mode--` badge class
 */
export function templateModeClass(mode) {
  switch (mode) {
    case RENDER_MODE.INTERNAL:
      return 'internal';
    case RENDER_MODE.INDEX_FRAGMENT:
      return 'fragment';
    default:
      return 'page';
  }
}

/** @param {string} mode */
function templateModePriority(mode) {
  switch (mode) {
    case RENDER_MODE.INDEX_FRAGMENT:
      return 1;
    case RENDER_MODE.INTERNAL:
      return 2;
    default:
      return 0;
  }
}

/**
 * Orders templates for the template list: pages, then index fragments, then internal templates,
 * each group sorted by name (case-insensitive).
 *
 * @param {ClientTemplateSummary[]} templates
 * @returns {ClientTemplateSummary[]} a sorted copy
 */
export function sortTemplatesByMode(templates) {
  return [...templates].sort((left, right) => {
    const modeCompare = templateModePriority(left.mode) - templateModePriority(right.mode);
    if (modeCompare !== 0) {
      return modeCompare;
    }
    return left.name.localeCompare(right.name, undefined, {sensitivity: 'base'});
  });
}

/**
 * @param {string} mode
 * @returns {string} heading of the template list group for this mode
 */
export function templateModeGroupLabel(mode) {
  switch (mode) {
    case RENDER_MODE.INTERNAL:
      return 'Internal';
    case RENDER_MODE.INDEX_FRAGMENT:
      return 'Index Fragments';
    default:
      return 'Full Pages';
  }
}

/**
 * Keeps the templates whose name contains `query`, ignoring case and surrounding spaces.
 *
 * @param {ClientTemplateSummary[]} templates
 * @param {string} query
 * @returns {ClientTemplateSummary[]}
 */
export function filterTemplatesByName(templates, query) {
  const needle = (query || '').trim().toLowerCase();
  if (!needle) {
    return templates;
  }
  return templates.filter((template) => template.name.toLowerCase().includes(needle));
}

/**
 * Splits templates into the template list groups: pages, then index fragments, then internal
 * templates, each sorted by name. Empty groups are left out.
 *
 * @param {ClientTemplateSummary[]} templates
 * @returns {{label: string, templates: ClientTemplateSummary[]}[]}
 */
export function groupTemplatesByMode(templates) {
  const groups = [];
  for (const template of sortTemplatesByMode(templates)) {
    const label = templateModeGroupLabel(template.mode);
    const last = groups[groups.length - 1];
    if (last?.label === label) {
      last.templates.push(template);
    } else {
      groups.push({label, templates: [template]});
    }
  }
  return groups;
}
